import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import FullCalendar from '@fullcalendar/react'
import { Box, CircularProgress, Paper, Tooltip, Typography, Chip } from '@mui/material'
import { DateTime } from 'luxon'
import { PaginatedResponse, Visit, VisitFilter } from 'models'
import { SnackbarProvider, useSnackbar } from 'notistack'
import React, { Component } from 'react'
import { VisitService } from 'services/visit.service'
import { customColors } from '../../customColors'

interface CalendarEvent {
  id: string
  title: string
  start: string
  end?: string
  extendedProps: {
    visit: Visit
  }
}

interface VisitCalendarState {
  visits: Visit[]
  loading: boolean
  error: string | null
  currentMonth: Date
}

interface VisitCalendarProps {
  enqueueSnackbar: (message: string, options?: { variant?: 'success' | 'error' | 'warning' | 'info' }) => void
}

class VisitCalendarComponent extends Component<VisitCalendarProps, VisitCalendarState> {
  private calendarRef = React.createRef<FullCalendar>()

  constructor(props: VisitCalendarProps) {
    super(props)
    
    // Inizializza currentMonth dai parametri URL o usa il mese corrente
    const urlParams = new URLSearchParams(window.location.search)
    const monthParam = urlParams.get('month')
    let initialMonth = new Date()
    
    if (monthParam) {
      const parsedDate = DateTime.fromFormat(monthParam, 'yyyy-MM')
      if (parsedDate.isValid) {
        initialMonth = parsedDate.toJSDate()
      }
    }

    this.state = {
      visits: [],
      loading: true,
      error: null,
      currentMonth: initialMonth
    }
  }

  componentDidMount() {
    this.updateUrlParameter()
    this.loadVisits()
  }

  componentDidUpdate(prevProps: VisitCalendarProps, prevState: VisitCalendarState) {
    if (prevState.currentMonth.getTime() !== this.state.currentMonth.getTime()) {
      this.updateUrlParameter()
      this.loadVisits()
    }
  }

  private updateUrlParameter = () => {
    const monthString = DateTime.fromJSDate(this.state.currentMonth).toFormat('yyyy-MM')
    const url = new URL(window.location.href)
    url.searchParams.set('month', monthString)
    window.history.replaceState({}, '', url.toString())
  }

  private loadVisits = () => {
    const startOfMonth = DateTime.fromJSDate(this.state.currentMonth).startOf('month')
    const endOfMonth = DateTime.fromJSDate(this.state.currentMonth).endOf('month')

    const filter: VisitFilter = {
      pageSize: 1000,
      pageId: 0,
      orderType: 'ASCENDING',
      orderBy: 'dataOra',
      startDate: startOfMonth.toISO() || undefined,
      endDate: endOfMonth.toISO() || undefined
    }

    this.setState({ loading: true, error: null })
    
    VisitService.filterVisits(
      filter,
      (response: { data: PaginatedResponse<Visit> }) => {
        const visitsData = response.data.items || []
        this.setState({ 
          visits: visitsData, 
          loading: false 
        })
      },
      (error: any) => {
        const errorMessage = error?.response?.data?.message || 'Errore nel caricamento dei sopralluoghi'
        this.setState({ 
          error: errorMessage, 
          loading: false, 
          visits: [] 
        })
        this.props.enqueueSnackbar(errorMessage, { variant: 'error' })
      }
    )
  }

  private generateEvents = (): CalendarEvent[] => {
    return this.state.visits
      .filter(visit => {
        if (!visit.dataOra) return false
        const dateTime = DateTime.fromISO(visit.dataOra)
        return dateTime.isValid
      })
      .map(visit => {
        const dateTime = DateTime.fromISO(visit.dataOra)
        return {
          id: visit.id,
          title: visit.asset?.address || 'Indirizzo non disponibile',
          start: dateTime.toISO() || visit.dataOra,
          extendedProps: { visit }
        }
      })
  }

  private handleEventClick = (info: any) => {
    const visit = info.event.extendedProps.visit
    const message = `Sopralluogo: ${visit.asset?.address || 'N/A'} - ${DateTime.fromISO(visit.dataOra).toFormat('dd/MM/yyyy HH:mm')}`
    this.props.enqueueSnackbar(message, { variant: 'info' })
  }

  private handleDatesSet = (dateInfo: any) => {
    // Calcola il mese centrale della vista invece di usare dateInfo.start
    // dateInfo.start può includere giorni del mese precedente per completare la griglia
    const viewStart = new Date(dateInfo.start)
    const viewEnd = new Date(dateInfo.end)
    const centerDate = new Date((viewStart.getTime() + viewEnd.getTime()) / 2)
    const newMonth = new Date(centerDate.getFullYear(), centerDate.getMonth(), 1)
    
    const currentMonthNormalized = new Date(
      this.state.currentMonth.getFullYear(), 
      this.state.currentMonth.getMonth(), 
      1
    )
    
    if (newMonth.getTime() !== currentMonthNormalized.getTime()) {
      setTimeout(() => {
        this.setState({ currentMonth: newMonth })
      }, 0)
    }
  }

  private renderEventContent = (eventInfo: any) => {
    const visit = eventInfo.event.extendedProps.visit
    const dateTime = DateTime.fromISO(visit.dataOra)
    const address = visit.asset?.address || 'Indirizzo non disponibile'
    const referente = visit.referente || 'Nessun referente'
    
    // Truncate address if too long
    const truncatedAddress = address.length > 30 ? address.substring(0, 30) + '...' : address
    
    const tooltipContent = (
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
          {dateTime.toFormat('dd/MM/yyyy HH:mm')}
        </Typography>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          <strong>Indirizzo:</strong> {address}
        </Typography>
        <Typography variant="body2">
          <strong>Referente:</strong> {referente}
        </Typography>
      </Box>
    )

    return (
      <Tooltip title={tooltipContent} arrow placement="top">
        <Box 
          sx={{ 
            p: 1,
            cursor: 'pointer',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            minHeight: '40px',
            borderRadius: '6px',
            backgroundColor: customColors.storebookGreen,
            color: 'white',
            border: `1px solid ${customColors.storebookDarkGreen}`,
            '&:hover': {
              backgroundColor: customColors.storebookDarkGreen,
              transform: 'translateY(-1px)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
            },
            transition: 'all 0.2s ease-in-out'
          }}
        >
          <Typography 
            variant="caption" 
            sx={{ 
              fontSize: '0.75rem', 
              fontWeight: 'bold',
              display: 'block',
              lineHeight: 1.2,
              color: 'white'
            }}
          >
            {dateTime.toFormat('HH:mm')}
          </Typography>
          <Typography 
            variant="caption" 
            sx={{ 
              fontSize: '0.7rem', 
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              lineHeight: 1.2,
              color: 'rgba(255, 255, 255, 0.95)',
              mt: 0.25
            }}
          >
            {truncatedAddress}
          </Typography>
          {referente && referente !== 'Nessun referente' && (
            <Chip
              label={referente.length > 15 ? referente.substring(0, 15) + '...' : referente}
              size="small"
              sx={{
                height: '16px',
                fontSize: '0.6rem',
                mt: 0.5,
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                '& .MuiChip-label': {
                  px: 0.5
                }
              }}
            />
          )}
        </Box>
      </Tooltip>
    )
  }

  render() {
    const { visits, loading, error } = this.state
    const events = this.generateEvents()

    return (
      <Box sx={{ p: 3 }}>
        <Paper elevation={3} sx={{ p: 3 }}>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h4" component="h1">
              Calendario Sopralluoghi
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {!loading && visits.length > 0 && (
                <Typography variant="body2" color="text.secondary">
                  {visits.length} sopralluoghi trovati
                </Typography>
              )}
              {loading && <CircularProgress size={24} />}
            </Box>
          </Box>

          {error && (
            <Box sx={{ mb: 2, p: 2, backgroundColor: '#ffebee', borderRadius: 1 }}>
              <Typography color="error" variant="body2">
                {error}
              </Typography>
            </Box>
          )}

          {!loading && !error && visits.length === 0 && (
            <Box sx={{ mb: 2, p: 3, textAlign: 'center', backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="body1" color="text.secondary">
                Nessun sopralluogo trovato per questo mese
              </Typography>
            </Box>
          )}

          <Box sx={{
            '& .fc': {
              fontFamily: 'inherit'
            },
            '& .fc-toolbar': {
              marginBottom: '1.5rem'
            },
            '& .fc-toolbar-title': {
              fontSize: '1.5rem',
              fontWeight: 500,
              color: customColors.storebookDarkBlue
            },
            '& .fc-button': {
              backgroundColor: customColors.storebookBlue,
              border: 'none',
              borderRadius: '6px',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: customColors.storebookDarkBlue,
                transform: 'translateY(-1px)',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              },
              '&:disabled': {
                backgroundColor: '#ccc'
              }
            },
            '& .fc-button-active': {
              backgroundColor: customColors.storebookDarkBlue,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            },
            '& .fc-daygrid-day': {
              minHeight: '120px',
              border: '1px solid #e0e0e0'
            },
            '& .fc-daygrid-day-number': {
              padding: '8px',
              fontSize: '0.9rem',
              fontWeight: 500,
              color: customColors.storebookDarkBlue
            },
            '& .fc-event': {
              backgroundColor: 'transparent !important',
              border: 'none !important',
              borderRadius: '0 !important',
              fontSize: '0.75rem',
              padding: '0 !important',
              margin: '1px !important',
              cursor: 'pointer'
            },
            '& .fc-event-main': {
              padding: '0 !important'
            },
            '& .fc-day-today': {
              backgroundColor: `rgba(54, 178, 170, 0.08) !important`,
              border: `2px solid ${customColors.storebookGreen} !important`
            },
            '& .fc-col-header-cell': {
              backgroundColor: customColors.storebookGreen,
              padding: '12px 0',
              fontWeight: 600,
              color: 'white',
              border: 'none'
            },
            '& .fc-col-header-cell-cushion': {
              color: 'white',
              fontWeight: 600
            },
            '& .fc-daygrid-day-top': {
              justifyContent: 'center'
            },
            '& .fc-more-link': {
              color: customColors.storebookBlue,
              fontWeight: 500,
              '&:hover': {
                color: customColors.storebookDarkBlue
              }
            },
            '& .fc-daygrid-more-link': {
              backgroundColor: customColors.storebookBlue,
              color: 'white',
              borderRadius: '4px',
              padding: '2px 6px',
              fontSize: '0.7rem',
              '&:hover': {
                backgroundColor: customColors.storebookDarkBlue
              }
            }
          }}>
            <FullCalendar
              ref={this.calendarRef}
              plugins={[dayGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              initialDate={this.state.currentMonth}
              locale="it"
              firstDay={1}
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: ''
              }}
              events={events}
              eventClick={this.handleEventClick}
              eventContent={this.renderEventContent}
              height="auto"
              dayMaxEvents={3}
              moreLinkText="altri"
              buttonText={{
                today: 'Oggi',
                month: 'Mese',
                week: 'Settimana',
                day: 'Giorno'
              }}
              datesSet={this.handleDatesSet}
            />
          </Box>
        </Paper>
      </Box>
    )
  }
}

// Wrapper con SnackbarProvider per gestire le notifiche
const VisitCalendarWrapper: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar()
  
  return <VisitCalendarComponent enqueueSnackbar={enqueueSnackbar} />
}

const VisitCalendar: React.FC = () => {
  return (
    <SnackbarProvider maxSnack={3}>
      <VisitCalendarWrapper />
    </SnackbarProvider>
  )
}

export default VisitCalendar
